/**
 * 기사관리 제재 ($j.F.Admin.Member.Penalty)
 *
 * 죄목·처벌·전과 원장(g5_member_penalties)의 조회·부과·해제.
 *
 * 화면은 둘로 나뉜다.
 *   요약  열3 '상태 › 제재현황' 탭의 dl 두 줄 — 지금 무슨 처벌을 받고 있나만 답한다
 *   상세  [제재 관리] 모달 — 이력·검색·부과·해제
 * 처음엔 전부 탭에 세로로 쌓았다가 산만하고 스크롤이 생겨 갈랐다 (2026-08 피드백).
 *
 * 모달은 `$j.Ui.JBModal` 이다 (상담내역 모달과 동일). 본문 마크업은 **여기서 만든다** —
 * 블레이드에 숨겨두면 v1/v2 두 화면에 같은 id 가 생기고, 모달을 다시 열 때 DOM 이 남아
 * DataTable 이 옛 노드를 잡는다. 열 때마다 새로 만들고 닫히면 통째로 사라지는 편이 안전하다.
 *
 * 그래서 모달 안 이벤트는 전부 **document 위임**이다 (열 때마다 노드가 바뀐다).
 *
 * 요약과 목록은 서버 응답으로만 그린다 — 부과/해제 후 화면을 직접 손대면 원장과 어긋난다.
 * 자동 죄목(환원)은 부과도 해제도 못 한다: 서버가 `canRelease=false` 로 내려주고
 * 죄목 선택지에도 없다 (PenaltyCharge::options).
 *
 * 설계: .ai/domains/penalty.md
 */
import Util from '../../../Common/Util';

const MODAL_ID = 'penalty-modal';

/** 확인·입력 다이얼로그 — 제재 모달 위에 겹쳐 뜬다 (JBModal 은 Layer 스택이다) */
const ASK_ID = 'penalty-ask';

/** 요주의 기준 — PenaltyService::WATCH_CASE_CNT 와 맞춘다 */
const WATCH = 3;

export default class Penalty {
    /**
     * @param {number} mbNo 회원번호
     * @param {object} sanc 셸이 이미 받아온 제재현황 (MemberEditFormService::sanctions)
     *                      — 요약값이 평평하게 들어 있고 charges(죄목 선택지)·whan*(환원 원본)도 온다
     */
    init(mbNo, sanc) {
        this.mbNo = Util.int(mbNo);
        if (!this.mbNo) return;

        this.sanc  = sanc || {};
        this.prefill = null;
        this.rel     = null;
        this.$table = null;

        this.drawSummary(this.sanc);
        this.bind();

        $('#mePenOpen').off('click.mepen').on('click.mepen', () => this.open());
    }

    /**
     * 회원을 지정해 모달만 연다 — 기사편집 밖(제재관리 목록·랭킹)에서 쓴다.
     *
     * `init()` 은 기사편집 셸이 이미 받아둔 제재현황을 넘겨받지만 여기엔 그런 게 없다.
     * 선택지(죄목·처벌)까지 `/adm/ajax/member/{mb}/penalties` 가 함께 내려주므로
     * 한 번 받아서 그대로 쓴다. 요약 dl(#mePenSum 등)은 그 화면에 없어 그리는 쪽이 no-op 다.
     *
     * @param {number}   mbNo
     * @param {string}   name     제목에 쓸 회원명 (없으면 #번호)
     * @param {Function} onChange 부과·해제 후 호출 — 부른 쪽 목록을 새로고침하라고
     * @param {object}   [prefill] 부과폼을 펼친 채로 연다 — { charge, reason, rel }
     *                   (예: -6차 부정발급 모니터링의 재발주 케이스 → 죄목 '부정발주')
     *                   죄목이 목록에 없으면 무시한다 — 선택지는 서버(PenaltyCharge)가 정본이다.
     *                   `rel: {table, id}` 는 근거 슬롯 — 이 모달에서 부과하는 제재에 붙는다.
     *                   부른 쪽이 "등록됐나" 를 원장으로 되짚을 수 있게 하는 실.
     *                   서버가 화이트리스트로 거른다 (PenaltyController::relOf).
     */
    openFor(mbNo, name, onChange, prefill) {
        this.mbNo = Util.int(mbNo);
        if (!this.mbNo) return;

        this.memName  = name || '';
        this.onChange = (typeof onChange === 'function') ? onChange : null;
        this.prefill  = prefill || null;
        this.rel      = (prefill && prefill.rel) || null;
        this.$table   = null;
        this.bind();

        $.get(`/adm/ajax/member/${this.mbNo}/penalties`)
            .done((res) => {
                this.sanc = { charges: res.charges || [], kinds: res.kinds || [], owner: res.owner || {} };
                this.caseRows = res.list || [];
                this.open();
            })
            .fail(() => $j.F.Admin.Util.tsalert('error', '제재 내역을 불러오지 못했습니다.'));
    }

    /**
     * 사건 하나를 **수정창만 띄워서** 연다 — 제재관리 목록(/adm/member/penalty)의 [수정] 자리.
     *
     * openFor() 가 "이 회원의 제재를 관리한다" 라면 이쪽은 "이 사건 한 건을 고친다" 다.
     * 목록에는 이미 회원·죄목·처벌·사유가 보이는데, 고치자고 회원 모달을 열면 CS 가
     * 방금 보던 줄을 표에서 다시 찾아야 했다. 목록의 행이 곧 사건이므로 바로 연다.
     *
     * 고치는 창·저장 경로는 모달과 **완전히 같은 것**을 쓴다(openCaseForm → saveEdit).
     * 죄목·처벌 선택지와 소속자 판정은 서버가 정본이라 여기서도 같은 곳에서 받아온다.
     *
     * 표(#penalty-table)는 없다 — openCaseForm 이 '지금 켜진 처벌'을 caseRows 에서 읽고,
     * _afterWrite 는 표가 없으면 onChange 만 부른다(부른 쪽 목록이 갱신된다).
     *
     * @param {number}   mbNo
     * @param {string}   name     제목에 쓸 회원명
     * @param {number}   caseNum  고칠 사건번호 (g5_member_cases.num)
     * @param {Function} onChange 저장 뒤 호출 — 부른 쪽 목록을 새로고침하라고
     */
    openCase(mbNo, name, caseNum, onChange) {
        this.mbNo = Util.int(mbNo);
        if (!this.mbNo || !Util.int(caseNum)) return;

        this.memName  = name || '';
        this.onChange = (typeof onChange === 'function') ? onChange : null;
        this.prefill  = null;
        this.rel      = null;
        this.$table   = null;   // 모달 표 없이 연다
        this.bind();

        $.get(`/adm/ajax/member/${this.mbNo}/penalties`)
            .done((res) => {
                this.sanc     = { charges: res.charges || [], kinds: res.kinds || [], owner: res.owner || {} };
                this.caseRows = res.list || [];

                // 소속자는 읽기전용 — 갈 곳(소유자)을 알려준다. 서버도 denySosok 으로 되돌린다
                if (this.isSosok()) {
                    return $j.F.Admin.Util.tsalert('error', `제재는 소유자에게만 합니다 — 소유자(${this.ownerLabel()}) 화면에서 해주세요.`);
                }

                const row = this.rowOfCase(Util.int(caseNum));
                if (!row) return $j.F.Admin.Util.tsalert('error', '수정할 사건을 찾을 수 없습니다.');

                this.openCaseForm(row);
            })
            .fail(() => $j.F.Admin.Util.tsalert('error', '제재 내역을 불러오지 못했습니다.'));
    }

    bind() {
        const self = this;
        const D = $(document);

        // 열 때마다 노드가 바뀌므로 document 위임이다. 두 번 불려도 되게 먼저 떼고 붙인다
        // (기사편집은 init 에서, 제재관리는 openFor 에서 부른다)
        D.off('.mepen');

        // ↓ 모달 안 — 열 때마다 노드가 새로 만들어지므로 위임으로 잡는다
        D.on('click.mepen',  '#mePenAdd', () => this.openCaseForm(null));
        // 죄목을 바꾸면 설명문구가 따라온다 (부과면 기본 처벌도 체크 — drawDesc 주석)
        D.on('change.mepen', '#mePenCharge', () => this.drawDesc($('.pen-modal__form--dlg').data('edit') === 1));
        D.on('keyup.mepen',  '#mePenKwd',    function () {
            if (self.$table) self.$table.search(this.value).draw();
        });
        D.on('keydown.mepen', '#mePenKwd', (e) => { if (e.key === 'Enter') e.preventDefault(); });
        D.on('change.mepen', '#mePenState', () => self.$table && self.$table.draw());
        D.on('click.mepen',  '#penalty-table .js-pen-release', function () {
            self.release(Util.int($(this).data('num')));
        });
        D.on('click.mepen',  '#penalty-table .js-pen-delete', function () {
            self.remove(Util.int($(this).data('num')));
        });
        D.on('click.mepen',  '#penalty-table .js-pen-edit', function () {
            self.openCaseForm(self.rowOfCase(Util.int($(this).data('case'))));
        });
    }

    destroy() {
        $('#mePenOpen').off('.mepen');
        $(document).off('.mepen').off('.mepenask');
        this.onChange = null;
        this._killTable();
    }

    // ───────────────────────────────────────────────── 확인·입력 다이얼로그

    /**
     * 브라우저 confirm/prompt 대신 쓰는 작은 모달.
     *
     * 네이티브 대화상자를 걷어낸 이유 셋 —
     *   1. 관리자 화면과 생김새가 따로 논다 (제재 모달 위에 OS 창이 뜬다)
     *   2. 사유는 여러 줄을 쓰는 칸인데 prompt 는 한 줄이다
     *   3. 크롬의 **'추가 대화상자 표시 안 함'** 을 한 번 체크하면 그 탭에서 해제·수정이
     *      통째로 막힌다 — 아무 반응 없이 실패하므로 CS 는 원인을 알 수 없다
     *
     * 제재 모달 위에 겹쳐 뜬다 ($j.Ui.JBModal → $j.Ui.Layer 스택).
     *
     * @param {object} o title · desc(HTML 허용, 호출부가 이스케이프) · value(있으면 입력칸)
     *                  · options(있으면 입력칸 대신 셀렉트 — [{value,label}])
     *                  · body(칸이 여러 개면 호출부가 만든 HTML — collect 와 짝) · placeholder
     *                  · okText · okClass · emptyMsg · onOk(값|true)
     *                  · collect() 확인 시 값을 만들어 돌려준다. 값이 없으면(경고까지 낸 뒤)
     *                    거짓을 돌려 창을 닫지 않는다 — 칸이 여러 개인 창이 쓴다
     */
    ask(o) {
        $j.Ui.JBModal.html(ASK_ID, this.askHtml(o), o.title || '확인', { width: o.width || '440px' });

        const D = $(document);
        D.off('.mepenask');                    // 이전 다이얼로그의 핸들러가 남지 않게

        const close = () => $j.Ui.JBModal.close(ASK_ID);
        const ok = () => {
            // 칸이 여러 개인 창은 호출부가 값을 만든다 (검증·경고도 거기서)
            if (o.collect) {
                const picked = o.collect();
                if (!picked) return;
                close();
                return o.onOk(picked);
            }

            // 입력칸이 없으면 확인 다이얼로그다 (구 confirm) → 값 대신 true 를 넘긴다
            if (o.value === undefined) { close(); return o.onOk(true); }

            const v = $.trim($('#mePenAskVal').val());
            if (!v) { $j.F.Admin.Util.tsalert('error', o.emptyMsg || '내용을 입력하세요.'); return $('#mePenAskVal').trigger('focus'); }

            close();
            o.onOk(v);
        };

        D.on('click.mepenask', '#mePenAskOk', ok);
        D.on('click.mepenask', '#mePenAskNo', close);
        // 여러 줄 칸이라 Enter 는 줄바꿈이다. 확인은 ⌘/Ctrl+Enter — 손을 떼지 않고 끝낸다
        D.on('keydown.mepenask', '#mePenAskVal', (e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); ok(); }
        });

        // 모달이 붙은 다음에 포커스를 준다. 커서는 글 끝 — 고치러 온 것이지 지우러 온 게 아니다
        setTimeout(() => {
            const el = document.getElementById('mePenAskVal');
            if (!el) return;
            el.focus();
            if (el.setSelectionRange) el.setSelectionRange(el.value.length, el.value.length);  // 셀렉트엔 없다
        }, 30);
    }

    askHtml(o) {
        let field = '';

        if (o.body) {
            field = o.body;             // 칸이 여러 개인 창 — 호출부가 만든다 (collect 와 짝)
        } else if (o.options) {
            // 고르는 칸 — 값이 정해져 있는 항목(죄목)은 자유입력이 아니라 선택이다
            const opts = o.options.map((c) =>
                `<option value="${this._esc(c.value)}"${c.value === o.value ? ' selected' : ''}>${this._esc(c.label)}</option>`
            ).join('');
            field = `<select class="me-select pen-ask__input" id="mePenAskVal" style="width:100%">${opts}</select>`;
        } else if (o.value !== undefined) {
            field = `
            <textarea class="me-input pen-ask__input" id="mePenAskVal" rows="3"
                      maxlength="500" placeholder="${this._esc(o.placeholder || '')}">${this._esc(o.value)}</textarea>`;
        }

        return `
        <div class="pen-ask">
            ${o.desc ? `<div class="pen-ask__desc">${o.desc}</div>` : ''}
            ${field}
            <div class="pen-ask__foot">
                <button type="button" class="me-btn" id="mePenAskNo">취소</button>
                <button type="button" class="me-btn ${o.okClass || 'me-btn--accent'}" id="mePenAskOk">${this._esc(o.okText || '확인')}</button>
            </div>
        </div>`;
    }

    // ───────────────────────────────────────────────── 요약 (탭)

    /**
     * 탭의 dl 두 줄.
     *   제재 = 지금 집행중인 처벌 (없으면 '없음')
     *   전과 = 해제된 것까지 포함한 누적. 요주의는 경고 누적 표시일 뿐 아무 처벌도 따라붙지 않는다
     */
    drawSummary(s) {
        const chips = this.chips(s);
        $('#mePenSum').html(chips);
        $('#mePenModalSum').html(chips);   // 모달이 열려 있으면 거기도 같이 갱신된다

        // 전과는 **사건(죄목) 수**다 — 처벌이 몇 개 붙었는지와 무관하다
        const by = (s.priorBy || []).map(p => `${this._esc(p.title)} ${Util.int(p.cnt)}`);
        const prior = [];
        if (Util.int(s.priorCnt) > 0) {
            // 건수는 제재관리 목록으로 가는 입구다 — 이 회원으로 좁혀서 연다.
            // 검색칸 규약이 '이름#번호' 라 그대로 넘기면 그 한 명만 걸린다
            // (CLAUDE.md '검색 인풋에 숨은 상태를 두지 않는다' · OrderListService::resolveMemberSearch).
            // 상태는 전체로 연다 — 전과는 해제된 것까지 세므로 기본값(집행중)이면 숫자가 안 맞는다.
            const who = this.memName || (window.MEMBER && MEMBER.mem_name) || '';
            const q = `/adm/member/penalty?mem=${encodeURIComponent(`${who}#${Util.int(this.mbNo)}`)}&state=`;
            prior.push(`<a href="${q}" target="_blank" rel="noopener"
                           class="me-pen__prior-link" data-tip-hover data-tip-title="제재관리"
                           data-tip="이 회원의 제재 내역을 제재관리 목록에서 엽니다 (해제된 건 포함)."
                        ><b>${Util.int(s.priorCnt)}건</b></a>`);
            if (by.length) prior.push(by.join(' · '));
            if (s.isWatch) {
                prior.push(`<span class="me-badge me-badge--danger" data-tip-hover data-tip-title="요주의"
                    data-tip="경고를 ${Util.int(s.warnCnt)}회 받았습니다(기준 ${WATCH}회). 표시일 뿐 자동으로 걸리는 제재는 없습니다.">요주의</span>`);
            }
            if (s.lastAt) prior.push(`<span style="color:#adb5bd">최근 ${this._esc(s.lastAt)}</span>`);
        }
        $('#mePenPrior').html(prior.length ? prior.join(' · ') : '<span style="color:#adb5bd">없음</span>');
    }

    /**
     * 집행중인 처벌 배지.
     *
     * 경고에는 누적 횟수를 붙이지 않는다 (2026-08 철회) — 칩은 '지금 무엇에 걸려 있나'만
     * 말하면 된다. 몇 번 받았는지는 바로 아래 전과 줄이 죄목별로 보여준다.
     */
    chips(s) {
        const out = (s.active || []).map((a) => {
            const n      = Util.int(a.cnt);
            const suffix = n > 1 ? ` ${n}` : '';

            return `<span class="me-badge me-badge--${a.level >= 2 ? 'danger' : 'warn'}">${this._esc(a.title)}${suffix}</span>`;
        });

        return out.length ? out.join(' ') : '<span class="me-badge me-badge--muted">없음</span>';
    }


    // ───────────────────────────────────────────────── 모달

    open() {
        this._killTable();

        const who  = this.memName || (window.MEMBER && MEMBER.mem_name) || '';
        const name = who ? `${who} #${this.mbNo}` : `#${this.mbNo}`;
        $j.Ui.JBModal.html(MODAL_ID, this.modalHtml(), `제재 관리 — ${this._esc(name)}`, { width: '900px' });

        this.applyPrefill();
        this.loadTable();
    }

    /**
     * 부른 쪽이 죄목·사유를 정해 보낸 경우 — 부과창을 그 값으로 열어준다.
     * 한 번 쓰고 버린다: 같은 회원 모달을 다시 열면 빈 창이어야 한다.
     */
    applyPrefill() {
        const p = this.prefill;
        this.prefill = null;
        if (!p || this.isSosok()) return;

        this.openCaseForm(null, p);
    }

    /**
     * 소속자 화면인가 — **제재는 소유자에게만 한다** (2026-08).
     *
     * 원장의 축이 소유자번호라, 소속자 화면에서 부과하면 조용히 사장에게 붙었다(형제 소속자
     * 전원이 같이 막힌다). 그래서 소속자 화면의 이 모달은 **읽기전용**이다 — 목록·요약은
     * 그대로 보여준다. 소속자가 일을 못 하는 이유가 대개 소유자 제재라 그건 여기서도 보여야 한다.
     * 서버도 같은 규칙으로 막는다 (PenaltyController::denySosok).
     */
    isSosok() {
        return !!(this.sanc && this.sanc.owner && this.sanc.owner.isSosok);
    }

    /** 안내에 쓸 소유자 표기 — "홍길동 #123" */
    ownerLabel() {
        const o = (this.sanc && this.sanc.owner) || {};

        return $.trim(`${o.ownerName || ''} #${Util.int(o.ownerNo)}`);
    }

    /** 모달 본문 — 툴바 + 표 + 환원 원본 각주 (부과·수정 폼은 겹쳐 뜨는 창이다 → openCaseForm) */
    modalHtml() {
        const s  = this.sanc;
        const ro = this.isSosok();

        const whanLast = s.whanLast ? ` · 최근 ${this._esc(s.whanLast)}` : '';

        return `
        <div class="pen-modal">
            <div class="pen-modal__bar">
                <div class="pen-modal__sum" id="mePenModalSum">${this.chips(s)}</div>
                <input type="text" class="me-input" id="mePenKwd" placeholder="죄목·사유·담당자 검색" style="width:180px">
                <select class="me-select" id="mePenState" style="width:auto">
                    <option value="active">집행중</option>
                    <option value="">전체</option>
                    <option value="off">해제됨</option>
                </select>
                ${ro
                    ? `<span class="me-pen__hint" data-tip-hover data-tip-title="제재는 소유자에게만"
                            data-tip="제재는 소유자 단위로 매깁니다. 여기서 부과하면 소속자 이름으로 보이지만 실제로는 사장님과 형제 소속자 전원이 막힙니다. 아래 목록은 소유자 ${this._esc(this.ownerLabel())} 의 제재입니다.">
                            읽기전용 — 부과·해제는 소유자(${this._esc(this.ownerLabel())}) 화면에서</span>`
                    : `<button type="button" class="me-btn me-btn--accent" id="mePenAdd">＋ 제재부과</button>`}
            </div>

            <table id="penalty-table" class="display" style="width:100%;border-collapse:collapse">
                <thead><tr></tr></thead><tbody></tbody>
            </table>

            <div class="pen-modal__foot">
                환원페널티 원본
                <span class="me-badge me-badge--${Util.int(s.whanActive) ? 'danger' : 'muted'}">적용중 ${Util.int(s.whanActive)}건</span>
                <span class="me-field__hint">누적 ${Util.int(s.whanTotal)}${whanLast}</span>
                <span class="me-field__hint">해제는 변제(수금완료)로만 됩니다.</span>
            </div>
        </div>`;
    }

    loadTable() {
        const self = this;

        this.$table = new DataTable('#penalty-table', {
            autoWidth: false,
            ajax: {
                url: `/adm/ajax/member/${this.mbNo}/penalties`,
                type: 'get',
                // ⚠️ GET 이라 브라우저가 캐시한다. 그대로 두면 부과·해제 뒤 ajax.reload() 를 해도
                //    옛 응답이 그려져 원장과 어긋난다 (정렬 바꾼 게 반영 안 돼 발견).
                cache: false,
                dataSrc: (json) => {
                    // 목록과 요약은 항상 같은 응답에서 온다 — 따로 갱신하면 어긋난다
                    self.sanc = Object.assign({}, self.sanc, json.summary || {});
                    self.drawSummary(self.sanc);
                    self.caseRows = json.list || [];   // openCaseForm 이 '지금 켜진 처벌'을 여기서 읽는다
                    return json.list || [];
                },
            },
            // 짧은 값(일자·죄목·담당)은 줄바꿈을 막는다. '2026-08-06' 이 두 줄로 접히면
            // 행 높이가 40 → 57px 로 뛰어 목록을 훑기 어렵다. 폭도 넉넉히 준다.
            // 남는 폭은 사유(width 미지정)가 전부 가져간다.
            columns: [
                { data: 'openedAt', title: '일자', width: '92px', className: 'js-c-date',
                  render: (v) => self._nowrap(v) || '<span style="color:#ced4da">날짜없음</span>' },
                { data: 'chargeTitle', title: '죄목', width: '104px', className: 'js-c-charge',
                  render: (d, t, r) => (t !== 'display')
                      ? String(r.chargeTitle || '')
                      : self._nowrap(r.chargeTitle) + self.caseEdit(r) },
                { data: null, title: '처벌', width: '150px',
                  render: (d, t, r) => self.punishCell(r) },
                // 검색(#mePenKwd)에는 사유 본문만 준다 — 근거 태그·연필의 툴팁 문구까지
                // 훑으면 '사건' 같은 흔한 낱말이 전 행에 걸린다
                { data: 'reason', title: '사유', className: 'js-c-reason',
                  render: (d, t, r) => (t !== 'display')
                      ? String(r.reason || '')
                      : (self._esc(r.reason) || '<span style="color:#adb5bd">사유 미기재</span>')
                        + self.srcTag(r) },
                { data: 'manageName', title: '담당', width: '68px', className: 'js-c-by',
                  render: (v) => self._nowrap(v) || '<span style="color:#adb5bd">시스템</span>' },
                { data: null, title: '상태', width: '104px', orderable: false,
                  render: (d, t, r) => self.stateCell(r) },
            ],

            // 정렬은 서버가 정한 순서를 그대로 쓴다 (사건 최근순 → 사건 안에서는 처벌 선언순서).
            //
            // ⚠️ 일자로 클라이언트 정렬을 걸면 안 된다. 한 사건의 처벌들은 **일자가 같아서**
            //    동점이 되고, DataTables 가 그 동점을 임의로 뒤집어 발주금지·수주금지 순서가
            //    사건마다 달라진다(〃 접기도 어긋난다).
            order: [],
            // 하단 = 좌 건수 · 우 페이지번호 (관리자 표 공통 규약)
            layout: { topStart: null, topEnd: null, bottomStart: 'info', bottomEnd: 'paging' },
            paging: true,
            pageLength: 10,      // 10개 단위 — 상담내역 모달과 같다
            lengthChange: false, // 개수 선택은 안 준다 (고정 10)
            searching: true,
            info: true,
            language: {
                info: '_START_–_END_ / 전체 _TOTAL_건',
                infoEmpty: '내역 없음',
                // 상태필터(집행중)가 기본값이라 이 문구는 늘 뜬다 — '검색'이라고 쓰면
                // 검색어를 넣은 줄 알게 된다. 걸러졌다는 사실만 알린다.
                infoFiltered: '(전체 _MAX_건 중)',
                emptyTable: '제재 이력이 없습니다.',
                zeroRecords: '검색 결과가 없습니다.',
                paginate: { first: '«', previous: '‹', next: '›', last: '»' },
            },
            headerCallback: (thead) => $(thead).find('th').css({
                'font-size': '13px', padding: '3px', 'text-align': 'center', 'font-weight': 'bold',
            }),
            rowCallback: (row, data) => {
                if (!data.active) $(row).css({ opacity: .55 });
                else if (data.level >= 2) $(row).css({ 'background-color': '#fff5f5' });
                else $(row).css({ 'background-color': '#fff9db' });
            },
            drawCallback: () => self.foldRepeats(),
        });

        this._bindStateFilter();
    }

    /**
     * 상태(집행중/해제됨) 필터.
     *
     * DataTables 전역 필터라 **이 테이블인지 확인하고** 걸러야 한다 — 같은 화면의
     * 상담내역 테이블(#counsel-table)까지 같이 걸리면 상담이 통째로 사라진다.
     * 등록은 한 번만 한다 (모달을 다시 열 때마다 쌓이면 필터가 중복 실행된다).
     */
    _bindStateFilter() {
        if (this._filterBound) return;
        this._filterBound = true;

        // 콜백 인자는 (설정, 검색용 문자열배열, 행인덱스, 원본 행객체) 순이다 — 넷째를 본다
        DataTable.ext.search.push(function (settings, searchData, idx, rowData) {
            if (settings.nTable.id !== 'penalty-table') return true;

            const want = $('#mePenState').val();
            if (!want) return true;

            return want === 'active' ? !!rowData.active : !rowData.active;
        });
    }

    /**
     * 표 인스턴스 정리.
     * 모달을 닫으면 DOM 이 통째로 사라지므로 destroy() 가 붙잡을 노드가 없다 —
     * 남은 노드가 있을 때만 부르고, 없으면 참조만 버린다.
     */
    _killTable() {
        if (!this.$table) return;
        if (document.getElementById('penalty-table')) {
            try { this.$table.destroy(); } catch (e) { /* 노드가 이미 사라진 경우 */ }
        }
        this.$table = null;
    }

    /**
     * 처벌칸 — 이제 1행 = 1처벌이다.
     * 경고는 막는 게 없다(blocks 가 빈 값) — 그 사실을 옆에 적어야
     * "경고인데 왜 발주가 되지?" 가 안 생긴다.
     */
    punishCell(p) {
        const badge = `<span class="me-badge me-badge--${p.level >= 2 ? 'danger' : 'warn'}" style="white-space:nowrap">${this._esc(p.punishTitle)}</span>`;
        // 짜고치기는 같은 패의 다른 참여자를 붙인다 (2명이면 예전 '↔ 상대' 와 같은 모양)
        const mates = (p.mates || []).map((m) => $j.F.Admin.Member.Pop.cell(Util.int(m.mbNo), m.memName));
        const tail  = mates.length
            ? `<span class="me-pen__hint">↔ ${mates.join(' · ')}</span>`
            : (p.blocks ? '' : '<span class="me-pen__mute">차단없음</span>');

        return `<span class="me-pen__row2">${badge} ${tail}</span>`;
    }

    /** 상태칸 — 집행중이면 해제버튼, 못 푸는 처벌은 이유, 해제됐으면 언제 풀렸는지 */
    stateCell(p) {
        if (p.canRelease) {
            // 소속자 화면은 읽기전용이다 — 여기서 풀면 소유자의 제재가 풀린다 (isSosok 주석)
            if (this.isSosok()) {
                return `<span class="me-pen__hint" data-tip-hover data-tip-title="집행중"
                    data-tip="해제는 소유자(${this._esc(this.ownerLabel())}) 화면에서 합니다.">소유자 화면에서</span>`;
            }

            return `<button type="button" class="me-btn js-pen-release" data-num="${Util.int(p.num)}">해제</button>`;
        }
        if (p.active && p.kind === 'PAIR_BAN') {
            return `<span class="me-pen__hint" data-tip-hover data-tip-title="수발주제한"
                data-tip="상대가 있는 처벌이라 등록·해제는 오더 > 수발주제한 화면에서 합니다.">화면 밖 관리</span>`;
        }
        if (p.active) {
            return `<span class="me-pen__hint" data-tip-hover data-tip-title="자동 해제"
                data-tip="시스템이 관리하는 처벌입니다. 변제(수금완료)하면 자동으로 풀립니다.">자동해제 대기</span>`;
        }

        const memo = this._esc(p.releaseMemo) || '사유 미기재';
        const by   = p.releaseName ? ` — ${this._esc(p.releaseName)}` : '';
        const tag  = `<span class="me-pen__hint" data-tip-hover data-tip-title="해제됨"
            data-tip="${memo}${by}">해제 ${this._esc(p.releasedAt)}</span>`;

        // 해제된 줄은 지울 수 있다 — 잘못 부과한 제재가 이력으로만 남아 계속 읽히는 걸 막는다.
        // 집행중인 줄에는 그리지 않는다 (차단이 걸린 채 근거만 사라진다). 소속자 화면은 읽기전용.
        if (this.isSosok()) return tag;

        // 상태칸이 104px 라 '해제 2026-08-01' 옆에 버튼을 두면 넘친다 → 줄바꿈을 허용한다
        return `<span class="me-pen__state">${tag}
            <button type="button" class="me-pen__del js-pen-delete" data-num="${Util.int(p.num)}"
                data-tip-hover data-tip-title="제재 삭제"
                data-tip="이 줄을 원장에서 지웁니다. 해제 이력도 함께 사라집니다.">삭제</button></span>`;
    }

    /**
     * 같은 사건이 이어지는 줄은 일자·죄목·사유·담당을 접는다 (죄목:처벌 = 1:N).
     *
     * **그리고 난 뒤에** 판단한다 — 서버가 '첫 행'을 정하면 페이지 경계에서 맥락이 끊긴다.
     * 2페이지 첫 줄이 〃 로 시작하면 무슨 사건인지 알 수 없다.
     */
    foldRepeats() {
        const self = this;
        if (!self.$table) return;

        let prev = null;
        $('#penalty-table tbody tr').each(function () {
            const row = self.$table.row(this).data();
            if (!row) return;                       // '내역 없음' 안내행

            if (prev !== null && row.caseNum === prev) {
                $(this).find('.js-c-date, .js-c-charge, .js-c-by').html('<span class="me-pen__ditto">〃</span>');
                $(this).find('.js-c-reason').empty();   // 사유는 길어서 반복하면 표가 시끄럽다
            }
            prev = row.caseNum;
        });
    }

    /**
     * 사건 수정(✎) — **죄목·처벌·사유를 한 창에서** 고친다 (openCaseForm).
     *
     * 전에는 셋이 각자 연필을 달고 있었다. 한 사건의 세 면이라 따로 고칠 이유가 없고,
     * 따로 저장하면 그 사이에 사건이 반만 바뀐 채로 남는다.
     *
     * 접힌 줄(`〃`)은 죄목칸을 통째로 바꾸므로 연필은 사건의 첫 줄에만 남는다.
     * 환원에는 달지 않는다 — 서버가 `canEditReason` 으로 정하고 눌러도 같은 조건으로 막는다.
     * 쌍 제한(짜고치기)이 집행중인 사건도 뺀다 — 상대가 있어 한 명만 바꿀 수 없다.
     */
    /** 사건번호 → 그 사건의 행 하나 (죄목·사유는 사건의 것이라 어느 줄이든 값이 같다) */
    rowOfCase(caseNum) {
        if (!caseNum) return null;

        return (this.caseRows || []).find((r) => Util.int(r.caseNum) === caseNum) || null;
    }

    caseEdit(r) {
        if (!r.canEditReason) return '';
        if (r.active && !r.canRelease) return '';   // 쌍 제한이 집행중인 사건
        if (this.isSosok()) return '';              // 읽기전용 (isSosok 주석)

        return ` <button type="button" class="me-pen__edit js-pen-edit" data-case="${Util.int(r.caseNum)}"
                    data-tip-hover data-tip-title="제재 수정"
                    data-tip="이 사건의 죄목·처벌·사유를 함께 고칩니다. 내리는 처벌은 지워지지 않고 해제 이력으로 남습니다.">✎</button>`;
    }

    /**
     * 근거 표시 — 이 사건이 어디서 왔는지.
     * 서버가 relTable/relId 를 내려주는데 화면이 안 쓰고 있었다.
     */
    srcTag(r) {
        const label = {
            counsel:    ['상담', '옛 상담내역 경고에서 열린 사건입니다. (상담 경고는 폐지됐고 지금은 여기서만 만듭니다)'],
            order:      ['오더', '환원(장비대금 미결제) 오더에서 열린 사건입니다.'],
            relation:   ['수발주제한', '오더 > 수발주제한 등록에서 열린 사건입니다.'],
            collusion:  ['부정발급 감시', '-6차 부정발급 모니터링의 케이스에서 등록한 제재입니다. 번호는 케이스번호입니다.'],
            cfg_baljoo: ['구 명단', '구 g5_config 발주금지 명단 이관건입니다.'],
            cfg_cash:   ['구 명단', '구 g5_config 출금금지 명단 이관건입니다.'],
        }[r.relTable];
        if (!label) return '';

        const id = Util.int(r.relId) ? ` #${Util.int(r.relId)}` : '';

        return ` <span class="me-pen__src" data-tip-hover data-tip-title="근거"
                    data-tip="${this._esc(label[1])}">${this._esc(label[0])}${id}</span>`;
    }

    // ───────────────────────────────────────────────── 부과·수정 창 (하나의 폼)

    /**
     * 사건 폼 — **부과와 수정이 같은 창**이다 (2026-08).
     *
     * 전에는 부과가 모달 안에 펼쳐지는 인라인 폼이었고, 수정은 죄목·처벌·사유마다 연필이 따로
     * 있어 창이 셋이었다. 고칠 것이 둘이면 두 번 저장해야 했고, 그 사이에 사건이 반만 바뀐
     * 상태로 남았다. 셋은 한 사건의 세 면이라 같이 고치는 것이 맞다.
     *
     * @param {object|null} row  수정할 사건의 행 데이터 (null = 새 부과)
     * @param {object} [pre]     부과 때 미리 채울 값 { charge, reason } (openFor 의 prefill)
     */
    openCaseForm(row, pre) {
        if (this.isSosok()) return;   // 읽기전용 — 소유자 화면에서 한다 (isSosok 주석)

        const s    = this.sanc;
        const edit = !!row;
        const cur  = edit ? { charge: String(row.charge || ''), reason: String(row.reason || '') }
                          : { charge: (pre && pre.charge) || '', reason: (pre && pre.reason) || '' };

        // 수정이면 지금 집행중인 처벌이 체크된 채로 열린다.
        // 출처가 표가 아니라 caseRows 인 이유: 제재관리 목록에서 [수정]으로 곧장 열면
        // 이 모달의 표(#penalty-table)가 아예 없다 (openCase 참고).
        const on = edit
            ? (this.caseRows || [])
                  .filter((r) => Util.int(r.caseNum) === Util.int(row.caseNum) && r.active).map((r) => r.kind)
            : [];

        const charges = (s.charges || []).map((c) =>
            `<option value="${this._esc(c.charge)}" data-desc="${this._esc(c.desc)}"
                     data-defaults="${this._esc((c.defaults || []).join(','))}"
                     ${c.charge === cur.charge ? 'selected' : ''}>${this._esc(c.title)}</option>`
        ).join('');

        // 처벌은 죄목이 고정하지 않는다 — 부과 때는 죄목을 고르면 기본값이 체크되고 CS 가 가감한다
        const kinds = (s.kinds || []).map((k) =>
            `<label class="pen-modal__kind"><input type="checkbox" class="js-pen-kind"
                value="${this._esc(k.kind)}" ${on.indexOf(k.kind) !== -1 ? 'checked' : ''}> ${this._esc(k.title)}</label>`
        ).join('');

        // 해제 사유는 수정에서만 — 부과에는 내릴 처벌이 없다
        const memoField = edit
            ? `<input type="text" class="me-input" id="mePenOffMemo" style="margin-top:8px"
                      placeholder="해제 사유 (처벌을 내릴 때만 — 내리지 않으면 비워둡니다)">`
            : '';

        this.ask({
            title:   edit ? '제재 수정' : '제재 부과',
            desc:    edit
                ? '이 사건의 죄목·처벌·사유를 함께 고칩니다. <b>내리는 처벌은 지워지지 않고</b> 해제 이력으로 남습니다.'
                : '',
            body: `
                <div class="pen-modal__form pen-modal__form--dlg" data-edit="${edit ? 1 : 0}">
                    <div class="pen-modal__formrow">
                        <select class="me-select" id="mePenCharge" style="width:170px">${charges}</select>
                    </div>
                    <textarea class="me-input pen-ask__input" id="mePenReason" rows="3" style="width:100%;margin-top:6px"
                              placeholder="사유 (오더번호·정황 등)">${this._esc(cur.reason)}</textarea>
                    <div class="pen-modal__kinds" id="mePenKinds">${kinds}</div>
                    <div class="pen-modal__desc" id="mePenDesc"></div>
                    ${memoField}
                </div>`,
            okText:  edit ? '저장' : '부과',
            okClass: 'me-btn--danger',
            width:   '560px',   // 사유 textarea 가 여러 줄이라 넓게
            collect: () => {
                const charge = $('#mePenCharge').val();
                const reason = $.trim($('#mePenReason').val());
                const picked = $('.js-pen-kind:checked').map((i, el) => el.value).get();
                const memo   = $.trim($('#mePenOffMemo').val());

                if (!charge) { $j.F.Admin.Util.tsalert('error', '죄목을 선택하세요.'); return false; }
                if (!picked.length) { $j.F.Admin.Util.tsalert('error', '처벌을 하나 이상 선택하세요.'); return false; }
                if (!reason) { $j.F.Admin.Util.tsalert('error', '사유를 입력하세요.'); $('#mePenReason').trigger('focus'); return false; }

                // 내리는 처벌이 있으면 해제 사유를 받는다 — 상태칸의 해제와 같은 이력이 남아야 한다
                const off = on.filter((k) => picked.indexOf(k) === -1);
                if (off.length && !memo) {
                    $j.F.Admin.Util.tsalert('error', '내리는 처벌이 있으면 해제 사유를 입력하세요.');
                    $('#mePenOffMemo').trigger('focus');
                    return false;
                }

                return { charge, reason, kinds: picked, memo };
            },
            onOk: (data) => (edit ? this.saveEdit(Util.int(row.caseNum), data) : this.saveAdd(data)),
        });

        this.drawDesc(edit);   // 설명문구 표시 (부과면 기본 처벌도 체크)
    }

    /**
     * 죄목 설명 + 기본 처벌 체크.
     * **수정에서는 체크를 건드리지 않는다** — 지금 집행중인 처벌을 죄목 바꿨다고 조용히
     * 갈아치우면 CS 가 의도하지 않은 해제·부과가 섞인다. 설명문구만 바꾼다.
     *
     * 부과 때 기본 체크는 죄목이 뭐든 **경고 고정**이다 — 죄목별 기본값(defaults)이
     * 수주금지 등 더 센 처벌을 자동으로 얹으면 CS 가 실수로 그대로 부과하기 쉽다.
     * 더 센 처벌이 필요하면 CS 가 직접 체크한다.
     * @param {boolean} keep true 면 체크 유지 (수정 모드)
     */
    drawDesc(keep) {
        const $sel = $('#mePenCharge option:selected');
        $('#mePenDesc').text($sel.data('desc') || '');
        if (keep) return;

        $('.js-pen-kind').each(function () { this.checked = this.value === 'WARN'; });
    }

    // ───────────────────────────────────────────────── 통신

    /** 새 사건 부과 */
    saveAdd({ charge, reason, kinds }) {
        // 근거는 모달을 연 쪽이 준 것 — 어느 죄목으로 바꿔 등록하든 출처는 그대로다
        const rel = this.rel ? { relTable: this.rel.table, relId: this.rel.id } : {};

        $.post(`/adm/ajax/member/${this.mbNo}/penalty/add`, Object.assign({ charge, reason, kinds }, rel))
            .done((res) => this._afterWrite(res, '제재 등록 중 오류가 발생했습니다.'))
            .fail(() => $j.F.Admin.Util.tsalert('error', '제재 등록 중 오류가 발생했습니다.'));
    }

    /** 사건 수정 — 죄목·처벌·사유를 한 번에 보낸다 (무엇을 더하고 내릴지는 서버가 정한다) */
    saveEdit(caseNum, { charge, reason, kinds, memo }) {
        $.post(`/adm/ajax/member/${this.mbNo}/penalty/update`, { caseNum, charge, reason, kinds, memo })
            .done((res) => this._afterWrite(res, '수정 중 오류가 발생했습니다.'))
            .fail(() => $j.F.Admin.Util.tsalert('error', '수정 중 오류가 발생했습니다.'));
    }

    /** 부과·수정 응답 처리 — 표와 요약은 서버 응답으로만 다시 그린다 */
    _afterWrite(res, errMsg) {
        if (!res || !res.result) return $j.F.Admin.Util.tsalert('error', (res && res.message) || errMsg);

        // 표는 모달로 열었을 때만 있다 (openCase 는 표 없이 수정창만 띄운다)
        if (this.$table) this.$table.ajax.reload(null, false);   // dataSrc 가 요약까지 다시 그린다
        if (this.onChange) this.onChange();     // 부른 쪽(제재관리 목록)도 갱신
        $j.F.Admin.Util.tsalert('success', res.message);
    }
    release(num) {
        if (!num) return;

        const row = this.$table ? this.$table.rows().data().toArray().find((r) => Util.int(r.num) === num) : null;
        const what = row ? `<b>${this._esc(row.chargeTitle)}</b> — ${this._esc(row.punishTitle)}<br>` : '';

        this.ask({
            title:       '제재 해제',
            // 행을 지우지 않는다는 사실을 여기서 말한다 — 해제가 기록을 없애는 일로 읽히면
            // CS 가 망설인다 (실제로는 해제이력이 남는 편이 안전하다)
            desc:        `${what}해제 사유를 남깁니다. 행은 지워지지 않고 <b>해제 이력</b>으로 남습니다.`,
            value:       '관리자 해제',
            placeholder: '해제 사유',
            okText:      '해제',
            okClass:     'me-btn--danger',
            emptyMsg:    '해제 사유를 입력하세요.',
            onOk: (memo) => {
                $.post(`/adm/ajax/member/${this.mbNo}/penalty/release`, { num, memo })
                    .done((res) => {
                        if (!res.result) return $j.F.Admin.Util.tsalert('error', res.message);
                        this.$table.ajax.reload(null, false);
                        if (this.onChange) this.onChange();
                        $j.F.Admin.Util.tsalert('success', res.message);
                    })
                    .fail(() => $j.F.Admin.Util.tsalert('error', '제재 해제 중 오류가 발생했습니다.'));
            },
        });
    }

    /**
     * 해제된 제재 삭제 — 해제(release)와 달리 **행이 없어진다.**
     *
     * 되돌릴 수 없으니 무엇을 지우는지 먼저 보여준다. 집행중인 줄에는 버튼 자체가 없지만
     * (stateCell) 그 사이 다시 부과됐을 수 있어 최종 판단은 서버가 한다.
     */
    remove(num) {
        if (!num) return;

        const row  = this.$table ? this.$table.rows().data().toArray().find((r) => Util.int(r.num) === num) : null;
        const what = row ? `<b>${this._esc(row.chargeTitle)}</b> — ${this._esc(row.punishTitle)}<br>` : '';

        this.ask({
            title:   '제재 삭제',
            desc:    `${what}이 줄을 원장에서 <b>지웁니다</b>. 해제 이력도 함께 사라지며 되돌릴 수 없습니다.`,
            okText:  '삭제',
            okClass: 'me-btn--danger',
            onOk: () => {
                $.post(`/adm/ajax/member/${this.mbNo}/penalty/delete`, { num })
                    .done((res) => {
                        if (!res.result) return $j.F.Admin.Util.tsalert('error', res.message);
                        this.$table.ajax.reload(null, false);
                        if (this.onChange) this.onChange();
                        $j.F.Admin.Util.tsalert('success', res.message);
                    })
                    .fail(() => $j.F.Admin.Util.tsalert('error', '제재 삭제 중 오류가 발생했습니다.'));
            },
        });
    }

    _esc(s) {
        return String(s ?? '').replace(/[&<>"']/g, c => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[c]));
    }

    /** 짧은 값을 한 줄로 묶는다. 빈 값이면 '' 를 돌려 호출부의 대체문구가 살아난다 */
    _nowrap(v) {
        const s = this._esc(v);
        return s ? `<span style="white-space:nowrap">${s}</span>` : '';
    }
}
